"use client";

import { useId, useState } from "react";
import { useLocale } from "next-intl";
import CopyCode from "@/components/Copy/CopyCode/CopyCode";
import BottomSheetModal from "@/components/ui/BottomSheetModal/BottomSheetModal";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";

// Libellés portés par le composant, comme les encadrés de transformHtml : un
// bloc de code s'affiche aussi dans des pages publiques et dans la messagerie
// flottante, dont les NextIntlClientProvider ne servent qu'un tri de clés.
const LABELS = {
    fr: { expand: "Agrandir le code", close: "Fermer" },
    en: { expand: "Expand code", close: "Close" },
};

const ICON_BUTTON_CLASS =
    "p-1.5 rounded-md text-gray-500 dark:text-grayDark-400 hover:text-gray-700 dark:hover:text-grayDark-200 hover:bg-gray-200 dark:hover:bg-bgDark-400 transition-colors";

const LINE_NUMBERS_CLASS =
    "[&_code]:[counter-reset:line] [&_.line]:before:[counter-increment:line] [&_.line]:before:content-[counter(line)] [&_.line]:before:inline-block [&_.line]:before:w-8 [&_.line]:before:mr-5 [&_.line]:before:text-right [&_.line]:before:select-none [&_.line]:before:text-gray-400 dark:[&_.line]:before:text-grayDark-400 [&_.line:last-child:empty]:before:content-none";

export default function CodeBlock({ language, code, highlightedHtml }) {
    const locale = useLocale();
    const labels = LABELS[locale] || LABELS.fr;
    const titleId = useId();
    const [isExpanded, setIsExpanded] = useState(false);

    return (
        <div className="border dark:border-borderDark rounded-xl overflow-hidden mb-3 relative w-full group">
            <div className="bg-gray-50 dark:bg-bgDark-550 md:px-4 px-2.5 py-2.5 flex justify-between items-center">
                <span className="uppercase text-xs text-gray-400 font-medium">
                    {language}
                </span>
                <div className="flex items-center gap-1">
                    <button
                        type="button"
                        onClick={() => setIsExpanded(true)}
                        className={ICON_BUTTON_CLASS}
                        aria-label={labels.expand}
                        title={labels.expand}
                    >
                        <SolarIcon
                            name="full-screen-linear"
                            className="w-4 h-4"
                        />
                    </button>
                    <CopyCode code={code} />
                </div>
            </div>
            <div
                dangerouslySetInnerHTML={{ __html: highlightedHtml }}
                className="[&>pre]:!m-0 [&>pre]:!rounded-none"
            />

            {/* portal : le bloc vit souvent sous un ancêtre transformé (fenêtre
                de discussion flottante), qui rognerait la modale. */}
            <BottomSheetModal
                isOpen={isExpanded}
                onClose={() => setIsExpanded(false)}
                maxWidthClass="sm:max-w-6xl"
                panelClassName="flex flex-col !overflow-hidden"
                zIndexClass="z-[110]"
                labelledBy={titleId}
                portal
            >
                <div className="bg-gray-50 dark:bg-bgDark-550 px-4 py-2.5 flex justify-between items-center shrink-0">
                    <span
                        id={titleId}
                        className="uppercase text-xs text-gray-400 font-medium"
                    >
                        {language}
                    </span>
                    <div className="flex items-center gap-1">
                        <CopyCode code={code} />
                        <button
                            type="button"
                            onClick={() => setIsExpanded(false)}
                            className={ICON_BUTTON_CLASS}
                            aria-label={labels.close}
                            title={labels.close}
                        >
                            <SolarIcon
                                name="close-circle-linear"
                                className="w-4 h-4"
                            />
                        </button>
                    </div>
                </div>
                {/* Numéros de ligne : un compteur CSS sur les .line de Shiki,
                    hors sélection pour ne pas partir avec un copier-coller. */}
                <div
                    dangerouslySetInnerHTML={{ __html: highlightedHtml }}
                    className={`min-h-0 flex-1 overflow-auto [&>pre]:!m-0 [&>pre]:!rounded-none [&>pre]:min-h-full [&>pre]:w-max [&>pre]:min-w-full ${LINE_NUMBERS_CLASS}`}
                />
            </BottomSheetModal>
        </div>
    );
}
